import { motion } from "framer-motion";
import {
  ClipboardList,
  Clock,
  SquareArrowOutUpRight,
} from "lucide-react";
import { useEffect, useState, useRef, useCallback } from "react";
import { TASK_STATUS, TASK_STATUS_LABELS } from "../Sites/Constants";
import { useNavigate } from "react-router-dom";
import { TASK_DETAILS } from "../../routes";
import { useDashboard } from "../Dashboard/Hooks/useDashboard";
import { Spinner } from "../../Components/Common";
import toast from "react-hot-toast";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { TOAST_MESSAGES } from "../Sites/Constants";


const Tabs = [TASK_STATUS.IN_PROGRESS, TASK_STATUS.PENDING];
const LIMIT = 5; // Items per page

const TasksDetails = () => {
  const [tasksData, setTasksData] = useState({
    [TASK_STATUS.IN_PROGRESS]: { results: [], offset: 0, hasMore: true },
    [TASK_STATUS.PENDING]: { results: [], offset: 0, hasMore: true },
  });
  const [activeTab, setActiveTab] = useState(TASK_STATUS.IN_PROGRESS);
  const [loading, setLoading] = useState(false);

  const { fetchTaskSummary } = useDashboard();
  const navigate = useNavigate();
  const observerRef = useRef();

  const fetchTaskData = async () => {
    if (loading || !tasksData[activeTab].hasMore) return;

    setLoading(true);
    try {
      const { offset } = tasksData[activeTab];
      const res = await fetchTaskSummary({
        status: activeTab,
        offset,
        limit: LIMIT,
      });
      if (res) {
        setTasksData((prev) => ({
          ...prev,
          [activeTab]: {
            results: [...prev[activeTab].results, ...res?.results],
            offset: prev[activeTab]?.offset + res?.results.length,
            hasMore: res?.results?.length === LIMIT,
          },
        }));
      }

    } catch (err) {
      toast.error(
          extractErrorMessage(err) || TOAST_MESSAGES.TASK_FETCH_FAILED
        );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch first page when switching tabs
    if (tasksData[activeTab]?.results?.length === 0) {
      fetchTaskData();
    }
  }, [activeTab]);

  const lastItemRef = useCallback(
    (node) => {
      if (loading) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && tasksData[activeTab].hasMore) {
          fetchTaskData();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [loading, tasksData, activeTab]
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="lg:col-span-2"
    >
      <div className="rounded-2xl shadow-md bg-white px-4 py-2">
        {/* Header */}
        <div className="flex justify-between mb-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Clock className="w-5 h-5" /> Tasks Details
          </h2>

          {/* Tabs */}
          <div className="flex justify-end overflow-x-auto">
            {Tabs.map((status) => (
              <button
                key={status}
                onClick={() => setActiveTab(status)}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors duration-200 whitespace-nowrap
                  ${
                    activeTab === status
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
              >
                {(TASK_STATUS_LABELS[status] || status) ===
                TASK_STATUS_LABELS.PENDING
                  ? TASK_STATUS_LABELS.UPCOMING
                  : TASK_STATUS_LABELS[status] || status}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        {activeTab && (
          <>
            {tasksData[activeTab].results.length > 0 ? (
              <ul className="space-y-3 h-[260px] overflow-y-auto">
                {tasksData[activeTab].results.map((task, idx) => {
                  const isLastItem =
                    idx === tasksData[activeTab]?.results?.length - 1;
                  return (
                    <li
                      key={task?.id}
                      ref={isLastItem ? lastItemRef : null}
                      className="bg-white rounded-lg shadow-sm transition-all duration-200 overflow-hidden border"
                    >
                      <div className="grid grid-cols-3 items-center p-2 border-black-2 shadow-md">
                        <h2
                          className="text-md font-semibold text-gray-800 max-w-[85%] truncate"
                          title={task.title}
                        >
                          {task.title}
                        </h2>
                        <span className="text-xs rounded-lg bg-blue-200 px-1.5 py-0.5 max-w-[100%] truncate">
                          Site: {task.site_name}
                        </span>
                        <div className="max-w-[100%] flex justify-end">
                          <SquareArrowOutUpRight
                            size={14}
                            onClick={() =>
                              navigate(
                                TASK_DETAILS.replace(":instanceId", task?.id)
                              )
                            }
                            className="text-blue-600 cursor-pointer hover:text-blue-800 transition-colors duration-200"
                          />
                        </div>
                      </div>
                    </li>
                  );
                })}
                {loading && (
                  <p className="text-gray-500 text-center h-full w-full">
                    <Spinner />
                  </p>
                )}
              </ul>
            ) : (
              <div className="w-full  h-[280px] flex justify-center items-center text-gray-300 flex-col">
                <ClipboardList size={35} />
                <span>No task</span>
              </div>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
};

export default TasksDetails;
